import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, set, push, update, remove } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { database, storage } from "../constants/firebase";
import { ICONS } from "../constants/icons";
import { convertToWebP, formatDate } from "../utils/utils";
import LoadingModal from "./LoadingModal";

const ManageTestimonialsModal = ({ isOpen, onClose, productId }) => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [showAddForm, setShowAddForm] = useState(false);
    
    // Manual Add Form State
    const [newTestimonial, setNewTestimonial] = useState({
        name: "",
        text: "",
        imageUrl: "",
        videoUrl: "",
        rating: 5,
        featured: true
    });
    const [manualImage, setManualImage] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !productId) return;

        const testimonialsRef = ref(database, `Listings/${productId}/testimonials`);
        const unsubscribe = onValue(testimonialsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({ id: key, ...data[key] }));
                setTestimonials(list.sort((a, b) => b.createdAt - a.createdAt));
            } else {
                setTestimonials([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [isOpen, productId]);

    if (!isOpen) return null;

    const handleToggleFeature = async (testimonialId, currentStatus) => {
        try {
            await update(ref(database, `Listings/${productId}/testimonials/${testimonialId}`), {
                featured: !currentStatus
            });
            window.showToast(!currentStatus ? "Testimonial featured!" : "Testimonial unfeatured", "success");
        } catch (error) {
            window.showToast("Failed to update status", "error");
        }
    };

    const handleDelete = async (testimonialId) => {
        if (!window.confirm("Are you sure you want to delete this testimonial?")) return;
        
        try {
            await remove(ref(database, `Listings/${productId}/testimonials/${testimonialId}`));
            window.showToast("Testimonial deleted", "success");
        } catch (error) {
            window.showToast("Failed to delete", "error");
        }
    };

    const handleManualSubmit = async (e) => {
        e.preventDefault();
        if (!newTestimonial.name || !newTestimonial.text) {
            window.showToast("Name and text are required", "error");
            return;
        }

        setIsSubmitting(true);
        setLoadingMessage("Adding testimonial...");

        try {
            let imageUrl = newTestimonial.imageUrl;
            
            if (manualImage) {
                setLoadingMessage("Uploading image...");
                const webpFile = await convertToWebP(manualImage);
                const storageReference = storageRef(storage, `testimonials/${productId}/manual_${Date.now()}_${webpFile.name}`);
                await uploadBytes(storageReference, webpFile);
                imageUrl = await getDownloadURL(storageReference);
            }

            const testimonialsRef = ref(database, `Listings/${productId}/testimonials`);
            const newRef = push(testimonialsRef);
            
            await set(newRef, {
                id: newRef.key,
                ...newTestimonial,
                imageUrl,
                isUserGenerated: false,
                createdAt: Date.now()
            });

            window.showToast("Testimonial added!", "success");
            setShowAddForm(false);
            setNewTestimonial({ name: "", text: "", imageUrl: "", videoUrl: "", rating: 5, featured: true });
            setManualImage(null);
        } catch (error) {
            window.showToast("Failed to add testimonial", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <LoadingModal showLoadingModal={isSubmitting} loadingMessage={loadingMessage} />
            
            <div className="bg-white dark:bg-[#1A1A1A] w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-800 flex flex-col animate-in fade-in zoom-in duration-300">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 dark:border-white/5 flex items-center justify-between bg-white dark:bg-[#1A1A1A]">
                    <div>
                        <h2 className="text-xl font-bold dark:text-white">Manage Testimonials</h2>
                        <p className="text-sm text-gray-500">Curate feedback shown on your course page</p>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {showAddForm ? (
                        <div className="space-y-4 animate-in slide-in-from-top duration-300">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-bold text-lg text-[#327691]">Add Manual Testimonial</h3>
                                <button onClick={() => setShowAddForm(false)} className="text-sm text-gray-500 hover:underline">Back to list</button>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input 
                                    type="text" 
                                    placeholder="Person's Name"
                                    value={newTestimonial.name}
                                    onChange={e => setNewTestimonial({...newTestimonial, name: e.target.value})}
                                    className="w-full rounded-xl px-4 py-3 bg-gray-50 dark:bg-white/5 text-black dark:text-white outline-none border border-gray-100 dark:border-gray-700"
                                />
                                <input 
                                    type="text" 
                                    placeholder="Video URL (Vimeo/Bunny/YT)"
                                    value={newTestimonial.videoUrl}
                                    onChange={e => setNewTestimonial({...newTestimonial, videoUrl: e.target.value})}
                                    className="w-full rounded-xl px-4 py-3 bg-gray-50 dark:bg-white/5 text-black dark:text-white outline-none border border-gray-100 dark:border-gray-700"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Rating</label>
                                <div className="flex gap-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setNewTestimonial({...newTestimonial, rating: star})}
                                            className="focus:outline-none transition-transform hover:scale-110"
                                        >
                                            <svg 
                                                width="24" 
                                                height="24" 
                                                viewBox="0 0 24 24" 
                                                fill={star <= newTestimonial.rating ? "#FFD700" : "none"} 
                                                stroke={star <= newTestimonial.rating ? "#FFD700" : "#D1D5DB"}
                                                strokeWidth="2"
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                            >
                                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                                            </svg>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            <textarea 
                                placeholder="Testimonial text..."
                                value={newTestimonial.text}
                                onChange={e => setNewTestimonial({...newTestimonial, text: e.target.value})}
                                rows="3"
                                className="w-full rounded-xl px-4 py-3 bg-gray-50 dark:bg-white/5 text-black dark:text-white outline-none border border-gray-100 dark:border-gray-700 resize-none"
                            ></textarea>

                            <div 
                                className="w-full cursor-pointer border-dashed border-2 border-gray-200 dark:border-gray-700 p-4 text-center rounded-xl bg-gray-50 dark:bg-white/5"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={e => setManualImage(e.target.files[0])} />
                                <p className="text-sm font-medium text-gray-500">
                                    {manualImage ? `Selected: ${manualImage.name}` : "Upload Screenshot (Optional)"}
                                </p>
                            </div>

                            <button 
                                onClick={handleManualSubmit}
                                className="w-full py-3 rounded-xl bg-[#327691] text-white font-bold hover:opacity-90 transition-opacity"
                            >
                                Add Testimonial
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <button 
                                onClick={() => setShowAddForm(true)}
                                className="w-full py-4 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-2xl flex items-center justify-center gap-2 text-gray-500 hover:border-[#327691] hover:text-[#327691] transition-all"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                Add Manual Testimonial
                            </button>

                            {loading ? (
                                <div className="text-center py-10 text-gray-500">Loading testimonials...</div>
                            ) : testimonials.length === 0 ? (
                                <div className="text-center py-10 text-gray-400">No testimonials yet.</div>
                            ) : (
                                <div className="grid gap-4">
                                    {testimonials.map(item => (
                                        <div 
                                            key={item.id}
                                            className="p-4 rounded-2xl bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/5 flex gap-4"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-bold text-sm truncate">{item.name}</span>
                                                    {item.isUserGenerated && (
                                                        <span className="text-[10px] bg-[#327691]/10 text-[#327691] px-1.5 py-0.5 rounded-full font-bold uppercase">Student</span>
                                                    )}
                                                    <span className="text-[10px] text-gray-400">{formatDate(item.createdAt)}</span>
                                                </div>
                                                <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{item.text}</p>
                                                <div className="mt-2 flex gap-2">
                                                    {item.imageUrl && <span className="text-[10px] text-[#327691]">📷 Has Image</span>}
                                                    {item.videoUrl && <span className="text-[10px] text-purple-500">🎥 Has Video</span>}
                                                </div>
                                            </div>
                                            
                                            <div className="flex flex-col gap-2">
                                                <button 
                                                    onClick={() => handleToggleFeature(item.id, item.featured)}
                                                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                                        item.featured 
                                                            ? 'bg-green-500 text-white shadow-lg shadow-green-500/20' 
                                                            : 'bg-gray-200 dark:bg-white/10 text-gray-500 dark:text-gray-400 hover:bg-[#327691] hover:text-white'
                                                    }`}
                                                >
                                                    {item.featured ? 'Featured' : 'Feature'}
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(item.id)}
                                                    className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors flex justify-center"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageTestimonialsModal;
